import { Component, OnInit, inject, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { NotificationService } from '../../core/notification.service';

/**
 * NotificationBadgeComponent displays a bell icon with an unread notification count badge.
 * Placed in the app header/sidebar area. Clicking emits an event to open the notification panel.
 *
 * Uses the NotificationService's unreadCount signal for reactive display.
 * Starts polling on init — the service handles its own lifecycle.
 *
 * Requirements: 3.4
 */
@Component({
    selector: 'app-notification-badge',
    imports: [CommonModule, TranslateModule],
    template: `
    <button
      class="notification-badge-btn"
      (click)="onBadgeClick()"
      [attr.aria-label]="'notifications.badge.ariaLabel' | translate:{ count: notificationService.unreadCount() }"
    >
      <!-- Bell icon (SVG) -->
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="bell-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>

      <!-- Unread count badge -->
      @if (notificationService.hasUnread()) {
        <span class="badge" aria-hidden="true">
          {{ notificationService.unreadCount() > 99 ? '99+' : notificationService.unreadCount() }}
        </span>
      }
    </button>
  `,
    styles: [`
    :host {
      display: inline-flex;
      align-items: center;
    }

    .notification-badge-btn {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2.5rem;
      height: 2.5rem;
      border: none;
      border-radius: 0.5rem;
      background: transparent;
      color: #4b5563;
      cursor: pointer;
      transition: background-color 0.15s ease, color 0.15s ease;
    }

    .notification-badge-btn:hover {
      background-color: #f3f4f6;
      color: #111827;
    }

    .notification-badge-btn:focus-visible {
      outline: 2px solid #3b82f6;
      outline-offset: 2px;
    }

    .bell-icon {
      width: 1.25rem;
      height: 1.25rem;
    }

    .badge {
      position: absolute;
      top: 0.125rem;
      right: 0.125rem;
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 1.125rem;
      height: 1.125rem;
      padding: 0 0.25rem;
      border-radius: 9999px;
      background-color: #ef4444;
      color: #ffffff;
      font-size: 0.625rem;
      font-weight: 600;
      line-height: 1;
    }

    :host-context(.dark) .notification-badge-btn {
      color: #9ca3af;
    }

    :host-context(.dark) .notification-badge-btn:hover {
      background-color: #374151;
      color: #f9fafb;
    }

    :host-context(.dark) .badge {
      background-color: #dc2626;
      color: #ffffff;
      box-shadow: 0 0 0 2px #1f2937;
    }
  `]
})
export class NotificationBadgeComponent implements OnInit {
  readonly notificationService = inject(NotificationService);

  /** Emits when the badge is clicked, signaling the parent to open/close the notification panel. */
  readonly panelToggled = output<void>();

  ngOnInit(): void {
    // Polling disabled until notifications backend route is deployed
    // this.notificationService.startPolling();
  }

  onBadgeClick(): void {
    this.panelToggled.emit();
  }
}
